import { UserApiResponse } from './ApiResponses';
import { BASE_API_URL, membershipState, userFilterType } from '../Enums';

/**
 * Queries the database for all users.
 * @param {string} token The jwt token for verification
 * @param {(number|'all'|null)} rowsPerPage How many users to return per page.
 * Accepts 10, 20, 50, or 'all'; anything else falls back to the server default.
 * @returns {UserApiResponse} Containing any error information or the array of
 * users.
 */
export async function getAllUsers({
  token,
  query = null,
  page = null,
  sortColumn = null,
  sortOrder = null,
  minRole = null,
  rowsPerPage = null,
}) {
  const url = new URL('/api/User/users', BASE_API_URL);

  if (sortColumn) {
    url.searchParams.set('sort', sortColumn);
  }

  if (sortOrder) {
    url.searchParams.set('order', sortOrder);
  }

  let status = new UserApiResponse();
  try {
    const res = await fetch(url.href, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        query,
        page,
        minRole,
        rowsPerPage,
      }),
    });
    if (res.ok) {
      const result = await res.json();
      status.responseData = result;
    } else {
      status.error = true;
    }
  } catch(err) {
    status.error = true;
  }
  return status;
}

export async function validateEventAdmins(token, ids) {
  let status = new UserApiResponse();
  const url = new URL('/api/User/admins/validate', BASE_API_URL);
  try {
    const res = await fetch(url.href, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ ids }),
    });
    const result = await res.json();
    status.responseData = result;
    if (!res.ok) {
      status.error = true;
    }
  } catch(err) {
    status.error = true;
  }
  return status;
}

/**
 * Edit an existing users
 * @param {Object} userToEdit - The user that is to be updated
 * @param {(string|undefined)} userToEdit._id - MongoDB id of the user
 * @param {(string|undefined)} userToEdit.firstName - The updated first name of
 * the user
 * @param {(string|undefined)} userToEdit.lastName - The updated last name of
 * the user
 * @param {string} userToEdit.email - Used to find the specific user to update
 * @param {(string|undefined)} userToEdit.password - The updated password of
 * the user
 * @param {(string|undefined)} userToEdit.major - The updated major of
 * the user
 * @param {(string|undefined)} userToEdit.discordUsername
 * @param {(string|undefined)} userToEdit.discordDiscrim
 * @param {(string|undefined)} userToEdit.discordID
 * @param {(string|undefined)} userToEdit.numberOfSemestersToSignUpFor
 * @param {(string|undefined)} userToEdit.doorCode - The updated password of
 * the user
 * @param {(string|undefined)} userToEdit.pagesPrinted - The updated password of
 * the user
 * @param {(string|undefined)} userToEdit.accessLevel - The updated password of
 * the user
 * @param {(string|undefined)} userToEdit.lastLogin - The updated password of
 * the user
 * @param {(string|undefined)} userToEdit.emailVerified - If the user's email
 * was verified
 * @param {(string|undefined)} userToEdit.emailOptIn - Opt into SCE's blast
 * week emails
 * @param {string} token - The jwt token for authentication
 * @returns {UserApiResponse} containing if the search was successful
 */
export async function editUser(userToEdit, token) {
  let status = new UserApiResponse();
  const {
    _id,
    firstName,
    lastName,
    email,
    password,
    major,
    numberOfSemestersToSignUpFor,
    doorCode,
    discordUsername,
    discordDiscrim,
    discordID,
    pagesPrinted,
    escrowPagesPrinted,
    accessLevel,
    lastLogin,
    emailVerified,
    emailOptIn,
  } = userToEdit;
  const url = new URL('/api/User/edit', BASE_API_URL);
  try {
    const res = await fetch(url.href, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        _id,
        firstName,
        lastName,
        email,
        password,
        major,
        numberOfSemestersToSignUpFor,
        doorCode,
        discordUsername,
        discordDiscrim,
        discordID,
        pagesPrinted,
        escrowPagesPrinted,
        accessLevel,
        lastLogin,
        emailVerified,
        emailOptIn
      })
    });
    if (res.ok) {
      const result = await res.json();
      status.responseData = result;
    } else {
      status.error = true;
    }
  } catch (err) {
    status.error = true;
    status.responseData = err.message || err;
  }
  return status;
}

/**
 * Change the access level of many users in one request.
 * @param {string[]} ids The MongoDB ids of the users to update
 * @param {number} accessLevel The membershipState value to apply to all of them
 * @param {string} token The jwt token for authentication
 * @returns {UserApiResponse} containing the number modified and any users that
 * were skipped because the editor outranked them
 */
export async function bulkEditUsers(ids, accessLevel, token) {
  let status = new UserApiResponse();
  const url = new URL('/api/User/bulkEdit', BASE_API_URL);
  try {
    const res = await fetch(url.href, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ ids, accessLevel }),
    });
    if (res.ok) {
      status.responseData = await res.json();
    } else {
      status.error = true;
    }
  } catch (err) {
    status.error = true;
    status.responseData = err.message || err;
  }
  return status;
}

/**
 * Deletes a user by an ID
 * @param {string} _id The ID of the user to delete
 * @param {string} token jwt token to authorize deletion
 * @returns {UserApiResponse} containing if the search was successful
 */
export async function deleteUserByID(_id, token) {
  let status = new UserApiResponse();
  const url = new URL('/api/User/delete', BASE_API_URL);
  try {
    const res = await fetch(url.href, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ _id }),
    });
    if (!res.ok) {
      status.error = true;
    }
  } catch(err) {
    status.error = true;
  }
  return status;
}

export async function getUserById(userID, token) {
  let status = new UserApiResponse();
  const url = new URL('/api/User/getUserById', BASE_API_URL);
  try {
    const res = await fetch(url.href, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ userID }),
    });
    if (res.ok) {
      const result = await res.json();
      status.responseData = result;
    } else {
      status.error = true;
    }
  } catch(err) {
    status.error = true;
  }
  return status;
}

export async function setUserEmailPreference(email, emailOptIn) {
  let status = new UserApiResponse();
  const url = new URL('/api/User/setUserEmailPreference', BASE_API_URL);
  try {
    const res = await fetch(url.href, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email,
        emailOptIn,
      }),
    });
    if (res.ok) {
      const result = await res.json();
      status.responseData = result;
    } else {
      status.error = true;
    }
  } catch(err) {
    status.error = true;
  }
  return status;
}

export async function getUserData(email) {
  let status = new UserApiResponse();
  const url = new URL('/api/User/getUserDataByEmail', BASE_API_URL);
  try {
    const res = await fetch(url.href, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });
    if (res.ok) {
      const result = await res.json();
      status.responseData = result;
    } else {
      status.error = true;
    }
  } catch(err) {
    status.error = true;
  }
  return status;
}

export async function getAllUserSubscribedAndVerified(token) {
  let status = new UserApiResponse();
  const url = new URL('/api/User/usersSubscribedAndVerified', BASE_API_URL);
  try {
    const res = await fetch(url.href, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    if (!res.ok) {
      status.error = true;
    }
  } catch(err) {
    status.error = true;
  }
  return status;
}

export async function getAllUsersValidVerifiedAndSubscribed(token) {
  let status = new UserApiResponse();
  const url = new URL('/api/User/usersValidVerifiedAndSubscribed', BASE_API_URL);
  try {
    const res = await fetch(url.href, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    if (res.ok) {
      const result = await res.text();
      status.responseData = result;
    } else {
      status.error = true;
    }
  } catch(err) {
    status.error = true;
  }
  return status;
}

export async function getApiKey(token) {
  let status = new UserApiResponse();
  const url = new URL('/api/User/apiKey', BASE_API_URL);
  try {
    const res = await fetch(url.href, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    if (res.ok) {
      const result = await res.json();
      status.responseData = result;
    } else {
      status.error = true;
    }
  } catch (error) {
    status.error = true;
  }
  return status;
}

export async function getNewPaidMembersThisSemester(token) {
  let status = new UserApiResponse();
  const url = new URL('/api/User/getNewPaidMembersThisSemester', BASE_API_URL);
  try {
    const res = await fetch(url.href, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    if (res.ok) {
      const result = await res.json();
      status.responseData = result;
    } else {
      status.error = true;
    }
  } catch (error) {
    status.error = true;
  }
  return status;
}
